import type { AcceptableValue } from '@nuxt/ui';
import type { FormSchemaField } from '#layers/block-engine';
import type { AnyItem } from '#layers/data-model/types/schema/item';
import { defineAsyncComponent } from 'vue';
import { BuiltinBlockIds } from '#layers/data-model/constants/builtin-block-ids';
import { PRIMITIVE_TYPES_NUMBER } from '#layers/data-model/constants/schema/primitive-type';
import { FilterOperator } from '#layers/data-model/types/filters';
import { createPrimitiveType } from '#layers/data-model/utils/field-types/configured-input-default';
import { getPrimitiveInputDefaults } from '#layers/data-model/utils/field-types/primitive-input-defaults';
import { getTypename } from '#layers/data-model/utils/schema/primitive-type';
import { defineFieldTypeTemplate } from '../../define';

const SelectFilterInputGroup = defineAsyncComponent(() => import('#layers/data-model/components/Select/FilterInputGroup.vue'));

export interface SelectFieldOption {
	value: AcceptableValue;
	label?: string;
	description?: string;
	icon?: string;
	color?: string;
}

export interface SelectFieldOptions {
	multiple?: boolean;
	options?: SelectFieldOption[];
}

const MULTIPLE_OPTION_TYPES = ['json'] as const;
const SINGLE_OPTION_TYPES = ['string',	...PRIMITIVE_TYPES_NUMBER, 'boolean', 'uuid'] as const;

export const selectOptionsFormSchema = (ctx: { options?: SelectFieldOption[]; valueInput?: { block: string; props?: AnyItem } }) => ({
	name: 'options',
	label: '$t:field_types.select.options.options.label',
	description: '$t:field_types.select.options.options.description',
	block: BuiltinBlockIds.InputRepeater,
	props: {
		schema: [
			{
				name: 'value',
				label: '$t:common.value',
				block: ctx.valueInput?.block ?? BuiltinBlockIds.Input,
				props: ctx.valueInput?.props,
			},
			{
				name: 'label',
				label: '$t:common.label',
				block: BuiltinBlockIds.Input,
			},
			{
				name: 'description',
				label: '$t:common.description',
				block: BuiltinBlockIds.Input,
			},
			{
				name: 'icon',
				label: '$t:common.icon',
				block: BuiltinBlockIds.InputIcon,
			},
			{
				name: 'color',
				label: '$t:common.color',
				block: BuiltinBlockIds.InputColor,
			},
		],
		displayBlock: {
			id: BuiltinBlockIds.DisplayLabels,
			props: {
				options: ctx.options,
			},
			valueKey: 'value',
		},
	},
} satisfies FormSchemaField<SelectFieldOptions, 'options'>);

export default defineFieldTypeTemplate<SelectFieldOptions>({
	type: 'primitive',
	id: 'select',
	name: '$t:field_types.select.name',
	description: '$t:field_types.select.description',
	icon: 'lucide:chevron-down',
	sort: 300,
	keywords: ['dropdown', 'enum', 'option', 'choice'],
	supportedPrimitiveTypes: ({ options }) => {
		if (options?.multiple === undefined) {
			return [...MULTIPLE_OPTION_TYPES, ...SINGLE_OPTION_TYPES];
		}

		return options.multiple ? MULTIPLE_OPTION_TYPES : SINGLE_OPTION_TYPES;
	},
	options: ({ form, context: { primitiveType, isNewField }, values }) => {
		const typeName = getTypename(primitiveType);
		const resolvedType = typeName !== 'json' ? primitiveType : createPrimitiveType('string');
		const valueInput = getPrimitiveInputDefaults(resolvedType);

		return form([
			{
				name: 'multiple',
				label: '$t:field_types.select.options.multiple.label',
				description: '$t:field_types.select.options.multiple.description',
				block: BuiltinBlockIds.InputSwitch,
				props: {
					disabled: !isNewField,
					// TODO: Translate this, once we have a way to translate the field type templates
					disabledNotice: 'This selection cannot be changed after creation of a field',
				},
				default: () => typeName === 'json',
			},
			{ ...selectOptionsFormSchema({ options: values.options ?? [], valueInput }), fieldProps: { layout: 'stacked' } },
		]);
	},
	filter: ({ options }) => options?.options?.length
		? {
				inputGroup: SelectFilterInputGroup,
				supportedOperators: [FilterOperator.EQ, FilterOperator.NEQ, FilterOperator.IN, FilterOperator.NIN],
			}
		: undefined,
});
